import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchMemoShares,
  fetchMemoShare,
  remoteMemoShareSync,
  memoShareKeys
} from '../api/memoShareAPI';
import { UIShareStatus } from '../../model/UiShapes';
import { apiShareStatusToUIShareStatus } from '../../model/DataConverters';
import { handleError, useErrorHandler } from '../managers/errorHandler';

// Memo分享列表查询
// 使用服务器端的 /memoShare/memos 接口
// 返回 List<MemoShareSummaryInfo> 数据结构
// 对应服务器端接口：MemoShareController.retrieveMemoShares()
export const useMemoShareListQuery = (options?: { enabled?: boolean }) => {
  const { handleError } = useErrorHandler();
  
  return useQuery({
    queryKey: memoShareKeys.list(),
    queryFn: async () => {
      try {
        const response = await fetchMemoShares();
        return response || [];
      } catch (error) {
        handleError(error, { context: '获取memo分享列表失败' });
        throw error;
      }
    },
    enabled: options?.enabled !== undefined ? options.enabled : true,
    staleTime: 5 * 60 * 1000, // 5分钟
    gcTime: 10 * 60 * 1000, // 10分钟,
  });
};

// 单个Memo分享查询
// 使用服务器端的 /memoShare/memo/{memoGUID} 接口
// 返回 MemoShareInfo 数据结构
// 对应服务器端接口：MemoShareController.retrieveMemoShareInfo()
export const useMemoShareDetailQuery = (memoId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: memoShareKeys.share(memoId),
    queryFn: async () => {
      const response = await fetchMemoShare(memoId);
      return response || null;
    },
    enabled: (options?.enabled !== undefined ? options.enabled : !!memoId),
    staleTime: 2 * 60 * 1000,
  });
};

// Memo分享同步操作
// 使用服务器端的 /memoShare/memo/sync 接口
// 对应服务器端接口：MemoShareController.syncMemoShare()
export const useMemoShareSyncMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (share: UIShareStatus) => remoteMemoShareSync(share),
    onSuccess: (data: any, variables: UIShareStatus) => {
      // 使相关查询失效，由Manager层处理数据同步
      queryClient.invalidateQueries({ queryKey: memoShareKeys.share(variables.guid) });
      queryClient.invalidateQueries({ queryKey: memoShareKeys.list() });
    },
    onError: (error) => {
      handleError(error, { context: 'useMemoShareSyncMutation', showToast: true });
    }
  });
};